import React, { useEffect, useId, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const FALLBACK_DOCUMENTS = {
  privacy: {
    document_type: 'privacy',
    title: 'Privacy Policy',
    last_updated: null,
    sections: [
      {
        id: 'fallback-privacy',
        title: 'Documento temporaneamente non disponibile',
        text:
          'Il contenuto completo della Privacy Policy non è al momento disponibile.\n\n' +
          'Per informazioni sul trattamento dei dati personali, contatta direttamente il titolare del sito.',
        position: 0,
      },
    ],
  },

  terms: {
    document_type: 'terms',
    title: 'Termini e Condizioni',
    last_updated: null,
    sections: [
      {
        id: 'fallback-terms',
        title: 'Documento temporaneamente non disponibile',
        text:
          'Il contenuto completo dei Termini e Condizioni non è al momento disponibile.\n\n' +
          'Per informazioni sulle condizioni applicabili, contatta direttamente il titolare del sito.',
        position: 0,
      },
    ],
  },

  cookie_policy: {
    document_type: 'cookie_policy',
    title: 'Cookie Policy',
    last_updated: null,
    sections: [
      {
        id: 'fallback-cookie-policy',
        title: 'Documento temporaneamente non disponibile',
        text:
          'Il contenuto completo della Cookie Policy non è al momento disponibile.\n\n' +
          'Il sito può comunque utilizzare funzionalità tecniche necessarie al proprio funzionamento.',
        position: 0,
      },
    ],
  },
};

const LEGAL_FOCUS_SELECTOR = '[data-legal-focus="true"]';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;

  width: 100%;
  height: 100%;

  padding: 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  overscroll-behavior: none;

  background: rgba(0, 0, 0, 0.78);

  animation: legal-overlay-enter 0.22s ease;

  @keyframes legal-overlay-enter {
    from {
      opacity: 0;
    }

    to {
      opacity: 1;
    }
  }

  @media (max-width: 600px) {
    padding: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Dialog = styled.section`
  width: min(100%, 1050px);
  height: min(90vh, 900px);
  min-width: 0;

  border: 1px solid #d7d7d7;
  border-radius: 20px;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  background: #ffffff;

  color: #222222;

  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.42);

  animation: legal-dialog-enter 0.25s ease;

  @keyframes legal-dialog-enter {
    from {
      opacity: 0;
      transform: translateY(18px) scale(0.98);
    }

    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  &:focus {
    outline: none;
  }

  @media (max-width: 600px) {
    width: 100%;
    height: 100%;

    border: 0;
    border-radius: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const DialogHeader = styled.header`
  position: relative;
  z-index: 2;

  min-width: 0;

  padding: 24px 78px 22px 30px;

  border-bottom: 1px solid #e3e3e3;

  background: #111111;

  color: #ffffff;

  @media (max-width: 600px) {
    padding: 20px 68px 18px 20px;
  }

  @media (max-width: 390px) {
    padding-left: 16px;
  }
`;

const DocumentTitle = styled.h1`
  min-width: 0;

  margin: 0;

  color: #ffffff;

  font-size: clamp(24px, 4vw, 38px);
  font-weight: 900;
  line-height: 1.12;

  overflow-wrap: anywhere;
`;

const LastUpdated = styled.p`
  margin: 10px 0 0;

  color: #c6c6c6;

  font-size: 13px;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  position: absolute;

  top: 18px;
  right: 20px;

  width: 44px;
  height: 44px;

  padding: 0;

  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 50%;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  background: rgba(255, 255, 255, 0.08);

  color: #ffffff;

  font-size: 30px;
  font-weight: 300;
  line-height: 1;

  cursor: pointer;

  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    border-color: #df2025;

    background: #df2025;

    transform: rotate(6deg);
  }

  &:focus-visible {
    outline: 3px solid #ffffff;
    outline-offset: 3px;

    border-color: #df2025;

    background: #df2025;
  }

  @media (max-width: 600px) {
    top: 14px;
    right: 14px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }
  }
`;

const DialogBody = styled.div`
  flex: 1;
  min-width: 0;

  padding: 34px 34px 60px;

  overflow-x: hidden;
  overflow-y: auto;

  overscroll-behavior: contain;

  touch-action: pan-y;

  -webkit-overflow-scrolling: touch;

  scrollbar-gutter: stable;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 3px solid #df2025;
    outline-offset: -6px;
  }

  @media (max-width: 600px) {
    padding: 28px 20px 50px;
  }

  @media (max-width: 390px) {
    padding-right: 16px;
    padding-left: 16px;
  }
`;

const DocumentContent = styled.div`
  width: min(100%, 860px);
  min-width: 0;

  margin: 0 auto;
`;

const DocumentSection = styled.section`
  min-width: 0;

  & + & {
    margin-top: 38px;
    padding-top: 34px;

    border-top: 1px solid #e4e4e4;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 16px;

  color: #151515;

  font-size: clamp(20px, 3vw, 27px);
  font-weight: 850;
  line-height: 1.25;

  overflow-wrap: anywhere;
`;

const SectionParagraph = styled.p`
  margin: 0;

  color: #3f3f3f;

  font-size: 16px;
  line-height: 1.75;

  white-space: pre-line;

  overflow-wrap: anywhere;

  & + & {
    margin-top: 18px;
  }

  @media (max-width: 600px) {
    font-size: 15px;
    line-height: 1.7;
  }
`;

function normalizeText(value, fallback = '') {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalizedValue = value.trim();

  return normalizedValue || fallback;
}

function normalizeDocumentType(value) {
  if (value === 'privacy' || value === 'terms' || value === 'cookie_policy') {
    return value;
  }

  return 'privacy';
}

function createParagraphs(value) {
  if (typeof value !== 'string') {
    return [];
  }

  return value
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

function normalizeSections(sections, fallbackSections) {
  if (!Array.isArray(sections) || sections.length === 0) {
    return fallbackSections;
  }

  const validSections = sections
    .map((section, index) => {
      if (!section || typeof section !== 'object') {
        return null;
      }

      const text = normalizeText(section.text);

      if (!text) {
        return null;
      }

      return {
        id: section.id ?? `legal-section-${index}`,
        title: normalizeText(section.title),
        text,
        position: Number.isFinite(Number(section.position)) ? Number(section.position) : 0,
      };
    })
    .filter(Boolean)
    .sort((firstSection, secondSection) => {
      if (firstSection.position !== secondSection.position) {
        return firstSection.position - secondSection.position;
      }

      return String(firstSection.id).localeCompare(String(secondSection.id));
    });

  return validSections.length > 0 ? validSections : fallbackSections;
}

function normalizeDocument(documentType, documentData) {
  const safeDocumentType = normalizeDocumentType(documentType);

  const fallbackDocument = FALLBACK_DOCUMENTS[safeDocumentType];

  if (!documentData || typeof documentData !== 'object') {
    return fallbackDocument;
  }

  return {
    document_type: safeDocumentType,
    title: normalizeText(documentData.title, fallbackDocument.title),
    last_updated: normalizeText(documentData.last_updated),
    sections: normalizeSections(documentData.sections, fallbackDocument.sections),
  };
}

function formatLastUpdated(value) {
  if (!value) {
    return '';
  }

  const dateParts = value.split('-');

  if (dateParts.length !== 3) {
    return value;
  }

  const year = Number(dateParts[0]);
  const month = Number(dateParts[1]);
  const day = Number(dateParts[2]);

  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return value;
  }

  const date = new Date(year, month - 1, day);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

function focusWithoutScrolling(element) {
  if (!(element instanceof HTMLElement)) {
    return;
  }

  try {
    element.focus({
      preventScroll: true,
    });
  } catch {
    element.focus();
  }
}

function getLegalFocusElements(dialogElement) {
  if (!(dialogElement instanceof HTMLElement)) {
    return [];
  }

  return Array.from(dialogElement.querySelectorAll(LEGAL_FOCUS_SELECTOR)).filter(
    (element) => element instanceof HTMLElement,
  );
}

function canScrollVertically(element, direction) {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const maximumScrollTop = element.scrollHeight - element.clientHeight;

  if (maximumScrollTop <= 0) {
    return false;
  }

  if (direction < 0) {
    return element.scrollTop > 0;
  }

  if (direction > 0) {
    return element.scrollTop < maximumScrollTop - 1;
  }

  return false;
}

function LegalDocumentModal({
  isOpen,
  documentType,
  documentData,
  closeLabel = 'Chiudi',
  onClose,
  returnFocusElement = null,
}) {
  const dialogRef = useRef(null);
  const dialogBodyRef = useRef(null);
  const closeButtonRef = useRef(null);

  const previousFocusRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const previousTouchYRef = useRef(null);

  const titleId = useId();

  const legalDocument = useMemo(
    () => normalizeDocument(documentType, documentData),
    [documentType, documentData],
  );

  const formattedLastUpdated = formatLastUpdated(legalDocument.last_updated);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const documentElement = window.document.documentElement;
    const body = window.document.body;
    const appRoot = window.document.querySelector('#root');

    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    previousFocusRef.current =
      returnFocusElement instanceof HTMLElement
        ? returnFocusElement
        : window.document.activeElement;

    const previousDocumentOverflow = documentElement.style.overflow;

    const previousDocumentOverscroll = documentElement.style.overscrollBehavior;

    const previousBodyPosition = body.style.position;
    const previousBodyTop = body.style.top;
    const previousBodyLeft = body.style.left;
    const previousBodyRight = body.style.right;
    const previousBodyWidth = body.style.width;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyPaddingRight = body.style.paddingRight;
    const previousBodyOverscroll = body.style.overscrollBehavior;

    const previousRootAriaHidden = appRoot?.getAttribute('aria-hidden') ?? null;

    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    if (scrollbarWidth > 0) {
      const currentPaddingRight =
        Number.parseFloat(window.getComputedStyle(body).paddingRight) || 0;

      body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }

    documentElement.style.overflow = 'hidden';
    documentElement.style.overscrollBehavior = 'none';

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = `-${scrollX}px`;
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';

    if (appRoot) {
      appRoot.setAttribute('aria-hidden', 'true');
    }

    const focusCloseButton = () => {
      focusWithoutScrolling(closeButtonRef.current);
    };

    const focusAnimationFrame = window.requestAnimationFrame(focusCloseButton);

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();

        onCloseRef.current?.();

        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusElements = getLegalFocusElements(dialogRef.current);

      if (focusElements.length === 0) {
        event.preventDefault();

        focusWithoutScrolling(dialogRef.current);

        return;
      }

      const firstElement = focusElements[0];
      const lastElement = focusElements[focusElements.length - 1];

      const activeElement = window.document.activeElement;

      const activeElementIsInside =
        activeElement instanceof Node && dialogRef.current?.contains(activeElement);

      if (!activeElementIsInside) {
        event.preventDefault();

        focusWithoutScrolling(event.shiftKey ? lastElement : firstElement);

        return;
      }

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();

        focusWithoutScrolling(lastElement);

        return;
      }

      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();

        focusWithoutScrolling(firstElement);
      }
    };

    const handleWheel = (event) => {
      const dialogBody = dialogBodyRef.current;

      if (
        !(dialogBody instanceof HTMLElement) ||
        !(event.target instanceof Node) ||
        !dialogBody.contains(event.target)
      ) {
        event.preventDefault();

        return;
      }

      if (!canScrollVertically(dialogBody, event.deltaY)) {
        event.preventDefault();
      }
    };

    const handleTouchStart = (event) => {
      if (event.touches.length !== 1) {
        previousTouchYRef.current = null;

        return;
      }

      previousTouchYRef.current = event.touches[0].clientY;
    };

    const handleTouchMove = (event) => {
      const dialogBody = dialogBodyRef.current;

      if (event.touches.length !== 1 || previousTouchYRef.current === null) {
        event.preventDefault();

        return;
      }

      const currentTouchY = event.touches[0].clientY;

      const scrollDirection = previousTouchYRef.current - currentTouchY;

      previousTouchYRef.current = currentTouchY;

      if (
        !(dialogBody instanceof HTMLElement) ||
        !(event.target instanceof Node) ||
        !dialogBody.contains(event.target)
      ) {
        event.preventDefault();

        return;
      }

      if (!canScrollVertically(dialogBody, scrollDirection)) {
        event.preventDefault();
      }
    };

    const handleTouchEnd = () => {
      previousTouchYRef.current = null;
    };

    window.document.addEventListener('keydown', handleKeyDown);

    window.document.addEventListener('wheel', handleWheel, {
      passive: false,
    });

    window.document.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });

    window.document.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });

    window.document.addEventListener('touchend', handleTouchEnd, {
      passive: true,
    });

    window.document.addEventListener('touchcancel', handleTouchEnd, {
      passive: true,
    });

    return () => {
      window.cancelAnimationFrame(focusAnimationFrame);

      window.document.removeEventListener('keydown', handleKeyDown);

      window.document.removeEventListener('wheel', handleWheel);

      window.document.removeEventListener('touchstart', handleTouchStart);

      window.document.removeEventListener('touchmove', handleTouchMove);

      window.document.removeEventListener('touchend', handleTouchEnd);

      window.document.removeEventListener('touchcancel', handleTouchEnd);

      documentElement.style.overflow = previousDocumentOverflow;

      documentElement.style.overscrollBehavior = previousDocumentOverscroll;

      body.style.position = previousBodyPosition;
      body.style.top = previousBodyTop;
      body.style.left = previousBodyLeft;
      body.style.right = previousBodyRight;
      body.style.width = previousBodyWidth;
      body.style.overflow = previousBodyOverflow;
      body.style.paddingRight = previousBodyPaddingRight;
      body.style.overscrollBehavior = previousBodyOverscroll;

      if (appRoot) {
        if (previousRootAriaHidden === null) {
          appRoot.removeAttribute('aria-hidden');
        } else {
          appRoot.setAttribute('aria-hidden', previousRootAriaHidden);
        }
      }

      window.scrollTo(scrollX, scrollY);

      const elementToRestore = previousFocusRef.current;

      window.requestAnimationFrame(() => {
        if (elementToRestore instanceof HTMLElement && window.document.contains(elementToRestore)) {
          focusWithoutScrolling(elementToRestore);
        }
      });
    };
  }, [isOpen, returnFocusElement]);

  if (!isOpen || !window.document.body) {
    return null;
  }

  return createPortal(
    <Overlay>
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <DialogHeader>
          <DocumentTitle id={titleId}>{legalDocument.title}</DocumentTitle>

          {formattedLastUpdated && (
            <LastUpdated>Ultimo aggiornamento: {formattedLastUpdated}</LastUpdated>
          )}

          <CloseButton
            ref={closeButtonRef}
            type="button"
            data-legal-focus="true"
            aria-label={closeLabel}
            title={closeLabel}
            onClick={() => onCloseRef.current?.()}
          >
            <span aria-hidden="true">×</span>
          </CloseButton>
        </DialogHeader>

        <DialogBody
          ref={dialogBodyRef}
          role="document"
          tabIndex={0}
          data-legal-focus="true"
          aria-label={`Contenuto: ${legalDocument.title}`}
        >
          <DocumentContent>
            {legalDocument.sections.map((section) => {
              const paragraphs = createParagraphs(section.text);

              return (
                <DocumentSection key={section.id}>
                  {section.title && <SectionTitle>{section.title}</SectionTitle>}

                  {paragraphs.map((paragraph, index) => (
                    <SectionParagraph key={`${section.id}-paragraph-${index}`}>
                      {paragraph}
                    </SectionParagraph>
                  ))}
                </DocumentSection>
              );
            })}
          </DocumentContent>
        </DialogBody>
      </Dialog>
    </Overlay>,
    window.document.body,
  );
}

export default LegalDocumentModal;
